// Centralized event catalog and property types for analytics (Amplitude v2)
// This supports the onboarding flows from the tracking plan (Figma):
// - Sign Up screens
// - Onboarding Hours
// - Onboarding Services

export const Events = {
    // Onboarding - Hours
    HOURS_SAVED_ONBOARDING: 'Hours Saved Onb',

    // Staff (one-shot marketplace level)
    ADD_STAFF_MEMBER_CLICKED: 'Add Staff Member Onb',
    STAFF_MEMBER_SAVED: 'Staff Member Saved Onb',
    STAFF_SCHEDULE_SAVED: 'Staff Schedule Saved Onb',

    // Services (one-shot marketplace level)
    ADD_SERVICE_CLICKED: 'CTA Add Service Onb',
    SAVE_SERVICE_CLICKED: 'CTA Saved Service Onb',

    // Clients (one-shot marketplace level)
    ADD_CLIENT_CLICKED: 'CTA Add Client Onb',
    ADD_CLIENT_CONTACT_CLICKED: 'Add Client Contact Onb',
    CLIENT_SAVED: 'Client Saved Onb',
    CLIENT_PET_SAVED: 'Client Pet Saved Onb',
    ADD_CLIENT_MEDICAL_HISTORY_CLICKED: 'Add Client Medical History Onb',

    // Bookings (one-shot marketplace level)
    CTA_BOOK_APPOINTMENT_CLICKED: 'CTA Book Appointment Onb',
    APPOINTMENT_BOOKED: 'Appointment Booked Onb',

    // Onboarding - Services
    ADD_SERVICE: 'add_service',
    ADD_SERVICE_PET_CATEGORY: 'add_service_pet_category',
    ADD_SERVICE_DETAIL: 'add_service_detail',
    ADD_SERVICE_PRICING: 'add_service_pricing',
    ADD_SERVICE_STAFF: 'add_service_staff',
    SAVE_SERVICE: 'save_service',

    // Sign Up (names to be confirmed from the tracking plan)
    // Example placeholders; replace/extend as needed
    SIGN_UP_STARTED: 'Sign Up Start',
    SIGN_UP_SUBMITTED: 'sign_up_submitted',
    SIGN_UP_COMPLETED: 'Sign Up Completed',
    SIGN_UP_VERIFIED: 'sign_up_verified',
    EMAIL_VERIFIED: 'Account Created',
    SIGN_UP_FAILED: 'Sign Up Failed',

    // Checkout flow (client side panel)
    CLIENT_INITIATED_CHECKOUT: 'Client Initiated Checkout',
    CLIENT_PAY_CLICKED: 'Client Pay'
} as const;

export type EventName = (typeof Events)[keyof typeof Events];

// Common event properties for the Services flow
export interface ServiceEventProperties {
    service_category_pet_type?: string; // e.g., Dog | Cat | Exotic
    service_pricing?: string; // pricing model or tier
    service_duration?: string | number; // minutes or textual duration
}

// Example properties for sign up events (to refine with product/analytics)
export interface SignUpEventProperties {
    source?: string; // e.g., organic, campaign name
    method?: string; // e.g., email, google, apple
    error_code?: string; // when failed
    country?: string;
    services?: string[]; // service ids selected during sign up
    new_business?: boolean;
    locations?: number | string; // selected locations option id
    groomers?: number | string; // selected groomers option id
    marketplace_id?: number | string;
    user_id?: number | string;
    staff_id?: number | string;
    sms_marketing_optin?: boolean;
}

// Checkout events properties
export interface CheckoutEventProperties {
    marketplace_id?: number | string;
    checkout_type?: string; // singlesale | membership | booking
    order_id?: number | string;
    booking_id?: number | string;
    invoice_id?: number | string;
    invoice_balance?: number; // current balance due at initiation or before pay click
    amount_to_pay?: number; // value entered when booking (may be partial)
    payment_method_id?: string | null; // Cash | CreditCard | POS | PayByLink | Other
    send_invoice?: boolean;
    email_present?: boolean; // whether an email was provided / valid
    tip_amount?: number; // numeric tip amount if any
    pos_terminal_id?: number | null;
    pos_service_id?: string | null;
}

export type EventProperties =
    | ServiceEventProperties
    | SignUpEventProperties
    | CheckoutEventProperties
    | Record<string, any>;
